import { Controller } from "@hotwired/stimulus"

// Sticky jump list for the settings cards: clicking an entry scrolls to its card, and the
// entry for the card currently in view stays highlighted.
export default class extends Controller {
  static targets = ["link", "card", "current"]
  static classes = ["active"]

  // Anchor line the highlighted card is measured against, in px from the viewport top.
  static ANCHOR = 96

  connect() {
    // Trailing throttle on a timer rather than requestAnimationFrame: rAF is suspended in a
    // background tab, which would leave the highlight unset until the tab is looked at.
    this.schedule = () => {
      if (this.pending) return
      this.pending = setTimeout(() => {
        this.pending = null
        this.updateHighlight()
      }, 50)
    }
    // The app shell scrolls an inner <main class="overflow-y-auto">, not the window, and
    // scroll events do not bubble — so listen on the document in the capture phase, which
    // catches them from whichever element actually scrolls.
    document.addEventListener("scroll", this.schedule, { capture: true, passive: true })

    // Belt and braces: the IntersectionObserver fires whenever a card crosses the reading
    // line regardless of which element owns the scroll, so the highlight still tracks even
    // where the scroll listener above never sees an event. Both only *trigger* the update —
    // the answer itself always comes from live geometry in updateHighlight().
    this.intersectionObserver = new IntersectionObserver(this.schedule, {
      rootMargin: `-${this.constructor.ANCHOR}px 0px -50% 0px`,
      threshold: 0,
    })

    // Cards are Turbo Frames swapped on every inline save, which changes their height;
    // re-measure when that happens rather than waiting for the next scroll.
    this.resizeObserver = new ResizeObserver(this.schedule)
    this.cardTargets.forEach((card) => this.observeCard(card))
    this.onHistory = () => this.followFragment()
    window.addEventListener("popstate", this.onHistory)
    window.addEventListener("hashchange", this.onHistory)
    this.followFragment()
    this.updateHighlight()
  }

  observeCard(card) {
    this.resizeObserver?.observe(card)
    this.intersectionObserver?.observe(card)
  }

  cardTargetConnected(card) {
    this.observeCard(card)
    this.schedule?.()
  }

  cardTargetDisconnected(card) {
    this.resizeObserver?.unobserve(card)
    this.intersectionObserver?.unobserve(card)
  }

  disconnect() {
    document.removeEventListener("scroll", this.schedule, { capture: true })
    this.resizeObserver?.disconnect()
    this.intersectionObserver?.disconnect()
    if (this.pending) clearTimeout(this.pending)
    window.removeEventListener("popstate", this.onHistory)
    window.removeEventListener("hashchange", this.onHistory)
    this.removeUnpinListeners()
  }

  jump(event) {
    event.preventDefault()
    const section = event.currentTarget.dataset.section
    const card = this.cardTargets.find((c) => c.dataset.section === section)
    if (!card) return

    const fragment = `#${card.id}`
    if (window.location.hash !== fragment)
      window.history.pushState({ ...window.history.state }, "", fragment)
    this.scrollToCard(card)

    // Pin the jumped-to entry until the reader scrolls themselves. Without this the
    // observer immediately reassigns the highlight — in a two-column masonry several
    // cards share the top of the viewport, so "what you clicked" is the only honest answer.
    this.pin(section)
  }

  pin(section) {
    this.pinned = section
    this.highlight(section)
    this.unpinOnScroll ||= () => {
      this.pinned = null
      this.updateHighlight()
    }
    this.removeUnpinListeners()
    window.addEventListener("wheel", this.unpinOnScroll, { once: true, passive: true })
    window.addEventListener("touchmove", this.unpinOnScroll, { once: true, passive: true })
    window.addEventListener("keydown", this.unpinOnScroll, { once: true })
  }

  followFragment() {
    const card = this.cardTargets.find((target) => `#${target.id}` === window.location.hash)
    if (!card) return
    this.scrollToCard(card, "auto")
    this.pin(card.dataset.section)
  }

  scrollToCard(card, behavior) {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    card.scrollIntoView({
      behavior: behavior || (reducedMotion ? "auto" : "smooth"),
      block: "start",
    })
    const heading = card.querySelector("h2, h3")
    if (heading) {
      heading.setAttribute("tabindex", "-1")
      heading.focus({ preventScroll: true })
    }
    // Close only the mobile outline; the actual settings cards remain untouched.
    this.linkTargets.forEach((link) => {
      const outline = link.closest("nav details")
      if (outline) outline.open = false
    })
  }

  removeUnpinListeners() {
    if (!this.unpinOnScroll) return
    window.removeEventListener("wheel", this.unpinOnScroll)
    window.removeEventListener("touchmove", this.unpinOnScroll)
    window.removeEventListener("keydown", this.unpinOnScroll)
  }

  // Measured from live geometry rather than from remembered intersection state: DOM order
  // is not visual order once the cards flow into two columns, and an IntersectionObserver
  // only reports crossings, so a scroll ending between two of them leaves a stale answer.
  //
  // The highlighted card is the one filling most of the reading area (anchor line down to
  // the viewport bottom) — what the reader is actually looking at. Measuring only the
  // distance to the anchor line picked a sliver of the previous card while the next one
  // filled the screen. Both ends of the page are fixed, read from the scroll position
  // itself: at the top the first card wins, at the end the last one does — a short final
  // card could otherwise never fill the most and never be highlighted.
  updateHighlight() {
    if (this.pinned) return

    const cards = this.cardTargets.map((card) => ({
      section: card.dataset.section,
      rect: card.getBoundingClientRect(),
    }))
    if (!cards.length) return

    const scroller = this.scrollContainer()
    if (scroller.scrollTop <= 0) {
      const first = cards.reduce((high, card) => (card.rect.top < high.rect.top ? card : high))
      this.highlight(first.section)
      return
    }
    if (scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1) {
      // Lowest card on screen, not the last in DOM order: in the two-column settings
      // masonry either column can end the page.
      const last = cards.reduce((low, card) => (card.rect.bottom > low.rect.bottom ? card : low))
      this.highlight(last.section)
      return
    }

    const anchor = this.constructor.ANCHOR
    const bottom = window.innerHeight
    const best = cards
      .map(({ section, rect }) => ({
        section,
        visible: Math.min(rect.bottom, bottom) - Math.max(rect.top, anchor),
      }))
      .filter(({ visible }) => visible > 0)
      .reduce((winner, card) => (!winner || card.visible > winner.visible ? card : winner), null)

    if (best) this.highlight(best.section)
  }

  // The element that actually scrolls: the app shell's inner <main>, or the document.
  scrollContainer() {
    for (
      let node = this.element.parentElement;
      node && node !== document.body;
      node = node.parentElement
    ) {
      const overflow = getComputedStyle(node).overflowY
      if ((overflow === "auto" || overflow === "scroll") && node.scrollHeight > node.clientHeight)
        return node
    }
    return document.scrollingElement || document.documentElement
  }

  highlight(section) {
    const current = this.linkTargets.find((link) => link.dataset.section === section)
    this.currentTargets.forEach((target) => {
      target.textContent = current?.textContent.trim() || ""
    })
    const activeClasses = this.hasActiveClass
      ? this.activeClasses
      : ["bg-green-50", "text-green-700"]
    this.linkTargets.forEach((link) => {
      const isActive = link.dataset.section === section
      if (isActive) link.setAttribute("aria-current", "location")
      else link.removeAttribute("aria-current")
      activeClasses.forEach((cls) => link.classList.toggle(cls, isActive))
      link.classList.toggle("text-gray-500", !isActive)
    })
  }
}
